import { normalizeSupplierId } from '@/components/RatesPage/types';

export const UNPUBLISHED_PREFIX = '🗑 ';

/**
 * Снят ли поставщик с публикации в Pimcore.
 * Контракт совпадает с work-calc PimcoreStagingSource::graphqlPublishedToBool:
 * false / 0 / '0' (а также 'false') → снят; всё остальное (включая undefined) → опубликован.
 */
export function isSupplierUnpublished(supplier: unknown): boolean {
  if (!supplier || typeof supplier !== 'object') {
    return false;
  }
  const published = (supplier as Record<string, unknown>).published;
  if (published === false || published === 0 || published === '0') {
    return true;
  }
  if (typeof published === 'string' && published.trim().toLowerCase() === 'false') {
    return true;
  }
  return false;
}

const SUPPLIER_NAME_KEYS = ['legal_name', 'fact_name', 'name'] as const;

/** Сырое имя без префикса 🗑 — ключ для сравнения/сортировки. */
export function supplierRawName(supplier: unknown): string {
  if (!supplier || typeof supplier !== 'object') {
    return '';
  }
  const record = supplier as Record<string, unknown>;
  const nameKey = SUPPLIER_NAME_KEYS.find((key) => {
    const value = record[key];
    return value != null && String(value).trim();
  });
  return nameKey ? String(record[nameKey]).trim() : '';
}

/**
 * Подпись поставщика для UI (без префикса 🗑).
 * Если в объекте нет имени — ищет в справочнике по id (запланированные операции с API).
 */
export function supplierDisplayName(
  supplier: unknown,
  directory?: unknown[],
): string {
  if (!supplier || typeof supplier !== 'object') {
    return '';
  }
  const record = supplier as Record<string, unknown>;
  if (record.showing_name != null && String(record.showing_name).trim()) {
    return String(record.showing_name).trim();
  }
  const direct = supplierRawName(supplier);
  if (direct) {
    return direct;
  }
  const id = normalizeSupplierId(record.id);
  if (id < 1 || !directory?.length) {
    return '';
  }
  const node = directory.find(
    (item) => normalizeSupplierId((item as Record<string, unknown>).id) === id,
  );
  return node ? supplierRawName(node) : '';
}

/** Подпись для v-select: префикс 🗑 для снятых с публикации. */
export function formatSupplierLabel(supplier: unknown): string {
  const name = supplierRawName(supplier);
  return isSupplierUnpublished(supplier) ? `${UNPUBLISHED_PREFIX}${name}` : name;
}

/**
 * Контракт сортировки (analytics-rates-supplier-names-unpublished.md):
 * 1) опубликованные выше, снятые с публикации — в конце (sortUnpublishedLast);
 * 2) внутри группы — алфавит по имени (locale ru, sensitivity base).
 */
export function compareSuppliers(a: unknown, b: unknown): number {
  const unpubA = isSupplierUnpublished(a);
  const unpubB = isSupplierUnpublished(b);
  if (unpubA !== unpubB) {
    return unpubA ? 1 : -1;
  }
  return supplierRawName(a).localeCompare(supplierRawName(b), 'ru', { sensitivity: 'base' });
}

export function sortSuppliers<T>(list: T[]): T[] {
  return [...list].sort(compareSuppliers);
}

/** id поставщиков из листа, которые снят с публикации (по справочнику allSuppliers). */
export function collectUnpublishedIds(
  supplierIds: unknown[] | null | undefined,
  directory: Array<Record<string, unknown>>,
): number[] {
  if (!supplierIds?.length) {
    return [];
  }
  const result = new Set<number>();
  supplierIds.forEach((raw) => {
    const id = normalizeSupplierId(raw);
    if (id < 1) {
      return;
    }
    const node = directory.find((s) => normalizeSupplierId(s.id) === id);
    if (node && isSupplierUnpublished(node)) {
      result.add(id);
    }
  });
  return [...result];
}
